// One flow lookup at a time. A new request cancels the one in flight, a late answer from a
// cancelled request is dropped, and unmounting cancels too.
import { useCallback, useEffect, useRef, useState } from 'react';
import { JobError } from '../services/dataJobs';
import type { JobStatusValue } from '../services/dataJobs';
import { fetchFlowAtPoint } from '../services/flowApi';
import type { FlowPointRequest, FlowRow } from '../services/flowApi';
import { describeRequestError, isAbortError } from '../services/http';

export type FlowLookupState =
  | { status: 'idle' }
  | { status: 'loading'; request: FlowPointRequest; jobStatus: JobStatusValue | null }
  | { status: 'done'; request: FlowPointRequest; rows: FlowRow[] }
  | { status: 'error'; request: FlowPointRequest; kind: 'timeout' | 'failed' | 'request'; message: string };

const keyOf = (r: FlowPointRequest) =>
  [r.country, r.lat, r.lon, r.window.start_time, r.window.end_time].join('|');

export function useFlowLookup(): {
  state: FlowLookupState;
  run(r: FlowPointRequest): void;
  retry(): void;
  cancel(): void;
} {
  const [state, setState] = useState<FlowLookupState>({ status: 'idle' });
  const controller = useRef<AbortController | null>(null);
  const requestId = useRef(0);
  const inFlightKey = useRef<string | null>(null);
  const lastRequest = useRef<FlowPointRequest | null>(null);

  const stop = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    requestId.current += 1;
    inFlightKey.current = null;
  }, []);

  const start = useCallback(
    (r: FlowPointRequest) => {
      stop();
      const ctl = new AbortController();
      controller.current = ctl;
      const id = requestId.current;
      inFlightKey.current = keyOf(r);
      lastRequest.current = r;
      setState({ status: 'loading', request: r, jobStatus: null });

      fetchFlowAtPoint(r, {
        signal: ctl.signal,
        onStatus: (jobStatus) => {
          if (id !== requestId.current) return;
          setState((prev) => (prev.status === 'loading' ? { ...prev, jobStatus } : prev));
        },
      }).then(
        (rows) => {
          if (id !== requestId.current) return;
          inFlightKey.current = null;
          setState({ status: 'done', request: r, rows });
        },
        (e: unknown) => {
          if (id !== requestId.current || isAbortError(e)) return;
          inFlightKey.current = null;
          if (e instanceof JobError) {
            setState({
              status: 'error',
              request: r,
              kind: e.kind === 'timeout' ? 'timeout' : 'failed',
              message: e.message,
            });
          } else {
            setState({ status: 'error', request: r, kind: 'request', message: describeRequestError(e) });
          }
        },
      );
    },
    [stop],
  );

  const run = useCallback(
    (r: FlowPointRequest) => {
      // The same lookup is already running (React's development double effect): keep it.
      if (inFlightKey.current === keyOf(r)) return;
      start(r);
    },
    [start],
  );

  const retry = useCallback(() => {
    if (lastRequest.current) start(lastRequest.current);
  }, [start]);

  const cancel = useCallback(() => {
    stop();
    setState((prev) => (prev.status === 'idle' ? prev : { status: 'idle' }));
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { state, run, retry, cancel };
}
